import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';

import { GateMode } from '../../core/api/api.types';
import { IconComponent } from '../../shared/components/icon.component';
import { PublicFooterComponent } from '../../public/public-footer/public-footer.component';
import { MaintenanceService } from '../../core/services/maintenance.service';
import { GateCountdownComponent } from './gate-countdown.component';
import { PrelaunchFormComponent } from './prelaunch-form.component';
import { PrelaunchLandingComponent } from './prelaunch-landing.component';

interface GateCopy {
  heading: string;
  body: string;
}

interface HeadingParts {
  before: string;
  brand: string;
  after: string;
}

/**
 * Full-page branded gate shown while the portal is offline.
 * Rendered above the router-outlet by AppComponent, so it covers every route.
 *
 * The header, signup form and footer are shared; the middle changes with the
 * gate mode an admin has selected.
 */
@Component({
  selector: 'app-maintenance-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IconComponent,
    PrelaunchFormComponent,
    GateCountdownComponent,
    PublicFooterComponent,
    PrelaunchLandingComponent,
  ],
  template: `
    @if (isPrelaunch()) {
      <app-prelaunch-landing
        [heading]="copy().heading"
        [body]="copy().body"
        [endsAt]="showCountdown() ? maintenance.endsAt() : null"
        (expired)="onCountdownExpired()"
      />
    } @else {
    <div class="min-h-screen bg-[#F6F8F7] text-slate-800 font-inter">
      <header class="bg-white/95 backdrop-blur-sm border-b border-slate-100/80">
        <div class="max-w-[1100px] mx-auto px-5 sm:px-8 h-[4.25rem] flex items-center justify-between gap-4">
          <a href="#top" class="flex items-center gap-2.5 min-w-0">
            <img
              src="/images/npr-favicon.png"
              alt=""
              class="size-10 rounded-full object-cover ring-1 ring-slate-200 shrink-0"
              width="40"
              height="40"
            />
            <span class="min-w-0">
              <span class="block text-[1.45rem] font-extrabold text-[#0B5C38] leading-none tracking-tight">NPR.ng</span>
              <span class="hidden sm:block text-[10px] text-slate-500 font-medium tracking-wide mt-0.5">
                News and Publication Registry
              </span>
            </span>
          </a>
          <p
            class="inline-flex items-center gap-2 rounded-full bg-white shadow-sm ring-1 ring-slate-200/80 px-3 py-1.5 text-[11px] sm:text-xs font-medium text-slate-600"
          >
            <svg viewBox="0 0 6 4" class="w-5 h-3.5 rounded-[2px] overflow-hidden shrink-0" aria-hidden="true">
              <rect width="2" height="4" fill="#008751" />
              <rect x="2" width="2" height="4" fill="#fff" />
              <rect x="4" width="2" height="4" fill="#008751" />
            </svg>
            Nigeria's No. 1 Platform
          </p>
        </div>
      </header>

        <section id="top" class="relative overflow-hidden">
          <div
            class="pointer-events-none absolute inset-0 opacity-40"
            style="background-image: radial-gradient(circle at 80% 20%, #d1fae5 0, transparent 45%), radial-gradient(circle at 10% 80%, #ecfdf5 0, transparent 40%)"
            aria-hidden="true"
          ></div>
          <div class="relative max-w-6xl mx-auto px-4 sm:px-6 py-10 lg:py-16">
            <div class="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
              <div>
                <h1 class="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
                  @if (headingParts(); as parts) {
                    {{ parts.before }}<span class="text-mint">{{ parts.brand }}</span>{{ parts.after }}
                  } @else {
                    {{ copy().heading }}
                  }
                </h1>
                <p class="mt-5 text-slate-600 text-base sm:text-lg leading-relaxed max-w-xl">
                  {{ copy().body }}
                </p>

                <div
                  id="features"
                  class="mt-6 flex items-start gap-3 rounded-xl bg-forest-50/80 ring-1 ring-forest-100 px-4 py-3"
                >
                  <app-icon name="envelope" class="w-5 h-5 text-forest-600 shrink-0 mt-0.5"></app-icon>
                  <p class="text-sm text-forest-800 leading-relaxed">
                    Be the first to know when we're back. Join our pre-launch list and get exclusive
                    updates.
                  </p>
                </div>

                @if (showCountdown()) {
                  <div class="mt-8 max-w-lg">
                    <app-gate-countdown
                      variant="light"
                      [endsAt]="maintenance.endsAt()"
                      (expired)="onCountdownExpired()"
                    ></app-gate-countdown>
                  </div>
                }

                <button
                  type="button"
                  (click)="recheck()"
                  [disabled]="maintenance.checking()"
                  class="mt-4 text-sm font-medium text-forest-600 hover:text-forest-800 disabled:opacity-70"
                >
                  {{ maintenance.checking() ? 'Checking…' : 'Check if we are back' }}
                </button>
              </div>

              <div class="relative">
                <img
                  src="/images/gate-maintenance-hero.png"
                  alt="Laptop showing a site under maintenance message with construction props"
                  class="w-full max-w-lg mx-auto lg:max-w-none"
                  width="960"
                  height="720"
                />
              </div>
            </div>
          </div>
        </section>

        <section id="subscribe" class="relative z-10 px-4 sm:px-6 -mt-2 pb-10">
          <div class="max-w-6xl mx-auto rounded-2xl bg-white shadow-lg ring-1 ring-black/5 px-5 py-5 sm:px-7 sm:py-6">
            <div class="flex flex-col xl:flex-row xl:items-center gap-4 xl:gap-6">
              <div class="flex items-center gap-3 shrink-0">
                <span
                  class="size-10 rounded-full bg-forest-50 text-forest-600 flex items-center justify-center"
                >
                  <app-icon name="bell" class="w-5 h-5"></app-icon>
                </span>
                <p class="font-semibold text-forest-800 text-sm sm:text-base">
                  Stay Updated — Subscribe to our pre-launch list
                </p>
              </div>
              <div class="flex-1 min-w-0">
                <app-prelaunch-form variant="bar"></app-prelaunch-form>
              </div>
            </div>
          </div>
        </section>

      <app-public-footer tone="forest" [flush]="true" [showAuthLinks]="false" footerId="contact" />
    </div>
    }
  `,
})
export class MaintenancePageComponent {
  readonly maintenance = inject(MaintenanceService);

  private static readonly FALLBACK: Record<GateMode, GateCopy> = {
    maintenance: {
      heading: "We're Improving NPR.ng for You!",
      body: "Our platform is currently undergoing scheduled maintenance to serve you better. We'll be back shortly with a faster, more secure and enhanced experience.",
    },
    prelaunch: {
      heading: 'Coming Soon!',
      body: "NPR.ng is Nigeria's official platform for the registration, management and verification of official publications.",
    },
  };

  private static readonly GENERIC_HEADINGS = new Set([
    'Under maintenance',
    "We're Improving NPR.ng for You!",
    "We're Improving the Partner Workspace for You!",
  ]);

  readonly isPrelaunch = computed(() => this.maintenance.mode() === 'prelaunch');

  readonly copy = computed<GateCopy>(() => {
    const fallback = MaintenancePageComponent.FALLBACK[this.maintenance.mode()];
    const heading = this.maintenance.heading();
    const body = this.maintenance.message();
    const useFallbackHeading =
      !heading ||
      (this.isPrelaunch() && MaintenancePageComponent.GENERIC_HEADINGS.has(heading));

    return {
      heading: useFallbackHeading ? fallback.heading : heading,
      body: body || fallback.body,
    };
  });

  readonly headingParts = computed<HeadingParts | null>(() => {
    const heading = this.copy().heading;
    const match = heading.match(/^(.*)(NPR\.ng)(.*)$/i);
    if (!match) {
      return null;
    }
    return { before: match[1], brand: match[2], after: match[3] };
  });

  readonly showCountdown = computed(
    () => this.maintenance.countdownEnabled() && !!this.maintenance.endsAt()
  );

  recheck(): void {
    if (this.maintenance.checking()) {
      return;
    }
    this.maintenance.check().subscribe();
  }

  onCountdownExpired(): void {
    this.recheck();
  }
}
